<template>
    <page :busy="loading" :compact-mobile-view="true">
        <b-container fluid class="guest-edit d-flex flex-column py-3">
            <validation-observer
                ref="form"
                v-slot="{ meta, setFieldValue, errors, handleSubmit }"
                as="span"
                validateOnMount
                :initial-values="guest"
                v-if="guest && metadata && detailsTabTitle"
            >
                <b-form @submit.prevent="(e) => handleSubmit(e, saveGuest)">
                    <b-row>
                        <b-col md="6">
                            <validation-provider name="email" rules="required|email">
                                <form-input
                                    floating-label="Email Address"
                                    type="email"
                                    v-model="guest.email"
                                    :disabled="!guestEdit"
                                    :left-icon="['fas', 'at']"
                                    :rightButton="{
                                        title: '',
                                        text: 'Merge Guest',
                                        icon: ['fas', 'down-left-and-up-right-to-center'],
                                        variant: 'outline-primary',
                                    }"
                                    @handle-right-button-click="$bvModal.show('merge-guest-modal-id')"
                                    :error="errors.email"
                                    @change="(val) => setFieldValue('email', val)"
                                ></form-input>
                            </validation-provider>
                        </b-col>
                        <b-col md="6">
                            <form-input
                                floating-label="Company"
                                v-model="guest.company"
                                :disabled="!guestEdit"
                                :left-icon="['fas', 'building']"
                            ></form-input>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="12" lg="2">
                            <form-select
                                floatingLabel="Title"
                                v-model="title"
                                :disabled="!guestEdit"
                                :leftIcon="['fas', 'user']"
                                :options="titleOptions"
                            ></form-select>
                        </b-col>
                        <b-col md="12" lg="4">
                            <validation-provider name="first_name" rules="required">
                                <form-input
                                    floating-label="First Name"
                                    v-model="guest.first_name"
                                    :disabled="!guestEdit"
                                    :left-icon="['fas', 'user']"
                                    :error="errors.first_name"
                                    @change="(val) => setFieldValue('first_name', val)"
                                ></form-input>
                            </validation-provider>
                        </b-col>
                        <b-col md="12" lg="6">
                            <validation-provider name="last_name" rules="required">
                                <form-input
                                    floating-label="Last Name"
                                    v-model="guest.last_name"
                                    :disabled="!guestEdit"
                                    :left-icon="['fas', 'user']"
                                    :error="errors.last_name"
                                    @change="(val) => setFieldValue('last_name', val)"
                                ></form-input>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="6">
                            <validation-provider name="date_of_birth">
                                <form-datepicker
                                    v-model="guest.date_of_birth"
                                    floating-label="Date of birth"
                                    :max="minDateOfBirth"
                                    :min="maxDateOfBirth"
                                    :hide-header="true"
                                    :reset-button="true"
                                    :display-format="userUnicodeDateFormat"
                                    :error="errors.date_of_birth"
                                    @change="(val) => setFieldValue('date_of_birth', val)"
                                ></form-datepicker>
                            </validation-provider>
                        </b-col>
                        <b-col md="6">
                            <validation-provider name="status_id">
                                <form-select
                                    floatingLabel="Guest's Status"
                                    v-model="guest.status_id"
                                    :leftIcon="['fas', 'user']"
                                    :options="statusesOptions"
                                    :error="errors.status_id"
                                    @change="(val) => setFieldValue('status_id', val)"
                                ></form-select>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col md="6">
                            <validation-provider name="address.address1">
                                <form-input
                                    name="address1"
                                    autocomplete="address1"
                                    v-model="guest.address.address1"
                                    :disabled="!guestEdit"
                                    floating-label="Address 1"
                                    :left-icon="['fas', 'location-dot']"
                                    :error="errors['address.address1']"
                                    @change="(val) => setFieldValue('address.address1', val)"
                                ></form-input>
                            </validation-provider>
                        </b-col>
                        <b-col md="6">
                            <form-input
                                name="address2"
                                autocomplete="address2"
                                v-model="guest.address.address2"
                                :disabled="!guestEdit"
                                floating-label="Address 2"
                                :left-icon="['fas', 'location-dot']"
                            ></form-input>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col class="col-12 col-md-6 col-lg-4">
                            <validation-provider name="address.country.id">
                                <form-select
                                    name="country"
                                    autocomplete="country"
                                    v-model="guest.address.country.id"
                                    :disabled="!guestEdit"
                                    floating-label="Country"
                                    :left-icon="loading ? undefined : ['fas', 'location-dot']"
                                    :left-button="loading"
                                    :options="countriesOptions"
                                    @change="onChangeCountry"
                                    :error="errors['address.country.id']"
                                    @input="(val) => setFieldValue('address.country.id', val)"
                                >
                                    <template #leftButton v-if="loading">
                                        <b-spinner small></b-spinner>
                                    </template>
                                </form-select>
                            </validation-provider>
                        </b-col>
                        <b-col class="col-12 col-md-6 col-lg-2">
                            <validation-provider name="zip">
                                <form-input
                                    name="postal-code"
                                    autocomplete="postal-code"
                                    v-model="guest.address.zip"
                                    :disabled="!guestEdit"
                                    floating-label="Zip / Postal code"
                                    :left-icon="loading ? undefined : ['fas', 'location-dot']"
                                    :left-button="loading"
                                    :error="errors['address.zip']"
                                    @input="handleZipChange"
                                >
                                    <template #leftButton v-if="loading">
                                        <b-spinner small></b-spinner>
                                    </template>
                                </form-input>
                            </validation-provider>
                        </b-col>
                        <b-col class="col-12 col-md-12 col-lg-6 mb-3">
                            Your city and state will be automatically added
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="6">
                            <validation-provider name="address.city">
                                <form-input
                                    name="address-level2"
                                    autocomplete="address-level2"
                                    v-model="guest.address.city"
                                    :disabled="!guestEdit"
                                    floating-label="City"
                                    :left-icon="loading ? undefined : ['fas', 'location-dot']"
                                    :left-button="loading"
                                    :error="errors['address.city']"
                                    @change="(val) => setFieldValue('address.city', val)"
                                >
                                    <template #leftButton v-if="loading">
                                        <b-spinner small></b-spinner>
                                    </template>
                                </form-input>
                            </validation-provider>
                        </b-col>
                        <b-col md="6">
                            <validation-provider name="address.state.id">
                                <form-select
                                    name="address-level1"
                                    autocomplete="address-level1"
                                    v-model="guest.address.state.id"
                                    floating-label="State / Region"
                                    :disabled="!guestEdit"
                                    :left-icon="loading ? undefined : ['fas', 'location-dot']"
                                    :left-button="loading"
                                    :error="errors['address.state.id']"
                                    :options="statesOptions"
                                    @change="(val) => setFieldValue('address.state.id', val)"
                                >
                                    <template #leftButton v-if="loading">
                                        <b-spinner small></b-spinner>
                                    </template>
                                </form-select>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row v-if="provinces && provinces.length > 0 && guest.address.province">
                        <b-col md="6">
                            <validation-provider name="address.province.id">
                                <form-select
                                    name="address-level3"
                                    autocomplete="address-level3"
                                    v-model="guest.address.province.id"
                                    :disabled="!guestEdit"
                                    floating-label="Province"
                                    :left-icon="loading ? undefined : ['fas', 'location-dot']"
                                    :left-button="loading"
                                    :error="errors['address.province.id']"
                                    :options="provincesOptions"
                                    @change="(val) => setFieldValue('address.province.id', val)"
                                >
                                    <template #leftButton v-if="loading">
                                        <b-spinner small></b-spinner>
                                    </template>
                                </form-select>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col md="6">
                            <validation-provider ref="mobilePhone" name="mobile_phone" rules="validPhoneNumber">
                                <b-button-group :class="['guest-edit__phone', !isMobile && 'desktop']">
                                    <form-phone-number-input
                                        v-model="guest.mobile_phone"
                                        :disabled="!guestEdit"
                                        inputId="mobile-phone"
                                        floating-label="Phone (mobile)"
                                        :default-country-code="defaultCountryCode"
                                        :error="errors.mobile_phone"
                                        @validate="onValidateMobilePhone"
                                        @input="(val) => onInputMobilePhone(val, 'mobile_phone')"
                                    />
                                    <responsive-button
                                        v-if="isMobile && guest.mobile_phone && mobilePhoneIsValid"
                                        :href="`tel:${guest.mobile_phone}`"
                                        :icon="['fas', 'phone']"
                                        variant="outline-primary"
                                    ></responsive-button>
                                </b-button-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="6">
                            <validation-provider
                                ref="homePhone"
                                name="address.phone"
                                v-slot="{ errors }"
                                rules="validPhoneNumber"
                            >
                                <b-button-group :class="['guest-edit__phone', !isMobile && 'desktop']">
                                    <form-phone-number-input
                                        v-model="guest.address.phone"
                                        :disabled="!guestEdit"
                                        inputId="home-phone"
                                        floating-label="Phone (home)"
                                        :default-country-code="defaultCountryCode"
                                        :error="errors['address.phone']"
                                        @validate="onValidateHomePhone"
                                        @input="(val) => onInputMobilePhone(val, 'address.phone')"
                                    />
                                    <responsive-button
                                        v-if="isMobile && guest.address.phone && homePhoneIsValid"
                                        :href="`tel:${guest.address.phone}`"
                                        :icon="['fas', 'phone']"
                                        variant="outline-primary"
                                    ></responsive-button>
                                </b-button-group>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="4">
                            <validation-provider
                                ref="workPhone"
                                name="work_phone"
                                v-slot="{ errors }"
                                rules="validPhoneNumber"
                            >
                                <b-button-group :class="['guest-edit__phone', !isMobile && 'desktop']">
                                    <form-phone-number-input
                                        v-model="guest.address.work_phone"
                                        :disabled="!guestEdit"
                                        inputId="work-phone"
                                        floating-label="Phone (work)"
                                        :default-country-code="defaultCountryCode"
                                        :error="errors['address.work_phone']"
                                        @validate="onValidateWorkPhone"
                                        @input="(val) => onInputMobilePhone(val, 'address.work_phone')"
                                    />
                                    <responsive-button
                                        v-if="isMobile && guest.address.work_phone && workPhoneIsValid"
                                        :href="`tel:${guest.address.work_phone}${guest.address.work_phone_ext}`"
                                        :icon="['fas', 'phone']"
                                        variant="outline-primary"
                                    ></responsive-button>
                                </b-button-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="2">
                            <validation-provider name="address.work_phone_ext">
                                <form-input
                                    v-model="guest.address.work_phone_ext"
                                    :disabled="!guestEdit"
                                    floating-label="Ext"
                                    :error="errors[0]"
                                />
                            </validation-provider>
                        </b-col>
                        <b-col md="6">
                            <validation-provider ref="fax" name="fax" v-slot="{ errors }" rules="validPhoneNumber">
                                <form-phone-number-input
                                    v-model="guest.address.fax"
                                    :disabled="!guestEdit"
                                    inputId="fax"
                                    floating-label="Fax"
                                    :default-country-code="defaultCountryCode"
                                    :error="errors['address.fax']"
                                    @validate="onValidateFax"
                                    @input="(val) => onInputMobilePhone(val, 'address.fax')"
                                />
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row class="mt-2">
                        <b-col lg="6">
                            <form-textarea
                                v-model="guest.note"
                                :disabled="!guestEdit"
                                floating-label="Notes"
                                :left-icon="['fas', 'note-sticky']"
                                rows="7"
                            />
                        </b-col>
                    </b-row>
                    <b-button
                        class="guest-edit__save-button mt-3 mb-3"
                        type="submit"
                        variant="primary"
                        :disabled="loading || !meta.valid || !guestEdit || busy"
                    >
                        Save
                    </b-button>
                    <b-button
                        class="ml-1"
                        variant="outline-primary"
                        v-if="guestEdit && metadata.permissions.billing_edit && guest.has_billing_data"
                        @click="$bvModal.show('clear-all-billing-data-modal-id')"
                        :disabled="loading"
                    >
                        Clear All Billing Data
                    </b-button>
                </b-form>
            </validation-observer>
        </b-container>
        <modal id="clear-all-billing-data-modal-id" title="Delete All Billing Data" @ok.prevent="deleteAllBillingData">
            This action will remove all cards/bank details for this guest. Are you sure you want to delete all billing
            data?
        </modal>
        <validation-observer ref="merge_guest_form" v-slot="{ meta: mergeFormMeta, errors: formErrors }">
            <modal
                id="merge-guest-modal-id"
                @ok.prevent="sendMergeGuests"
                :ok-disabled="!mergeFormMeta.valid || loading || !validTarget"
                :scrollable="false"
                title="Merge Guest"
                ok-title="Merge"
                @hidden="onHideModal"
            >
                <validation-provider name="target" rules="required">
                    <form-search-field
                        id="target"
                        :busy="searchBusy"
                        :value="selectedTargetEmail"
                        :options="targetEmailOptions"
                        @input="searchTargetEmail"
                        @select="selectTarget"
                        floating-label="Target Guest"
                        :error="formErrors['target']"
                    />
                </validation-provider>
                <span class="small text-danger"
                    >Warning: Proceed with caution. Data and reservations from current guest will be assigned to target
                    guest. This operation cannot be undone.</span
                >
            </modal>
        </validation-observer>
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import isEmpty from 'lodash/isEmpty';
    import debounce from 'lodash/debounce';
    import subYears from 'date-fns/subYears';
    import { parsePhoneNumber } from 'awesome-phonenumber';
    import API from '../../../api.jsx';
    import Page from '../../generic/page/page.vue';
    import Modal from '../../generic/modal/modal.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
    import FormDatepicker from '../../generic/form-datepicker/form-datepicker.vue';
    import FormSearchField from '../../generic/form-search-field/form-search-field.vue';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import { formatResponseError } from '../../../utils/api';
    import {
        GUEST_BY_ID_ENDPOINT,
        GUEST_CREATE_ENDPOINT,
        GUEST_DELETE_ALL_BILLING_DATA_ENDPOINT,
        GUEST_MERGE_DIFFERENT_EMAILS_ENDPOINT,
    } from '../../../endpoints.js';
    import set from 'lodash/set';
    import { defineAsyncComponent } from 'vue';
    import _ from 'lodash';

    const FormPhoneNumberInput = defineAsyncComponent(() =>
        import(
            /* webpackChunkName: "lx-form-phone-number-input" */
            '../../generic/form-phone-number-input/form-phone-number-input.vue'
        )
    );

    export default {
        name: 'GuestEdit',
        components: {
            Page,
            Modal,
            FormInput,
            FormSelect,
            FormTextarea,
            FormDatepicker,
            FormSearchField,
            ResponsiveButton,
            FormPhoneNumberInput,
        },
        data() {
            return {
                busy: false,
                loading: false,
                searchBusy: false,
                validTarget: false,
                mobilePhoneIsValid: false,
                homePhoneIsValid: false,
                workPhoneIsValid: false,
                targetEmailOptions: [],
                selectedTargetEmail: null,
                selectedTargetId: null,
                guestData: null,
            };
        },
        mounted() {
            (async () => {
                await this.fetchMetadata();
            })();
            if (!this.guest || !this.statuses || !this.countries || !this.states) this.loadData();
        },
        computed: {
            ...mapGetters('guest', [
                'metadata',
                'guest',
                'statuses',
                'countries',
                'states',
                'provinces',
                'detailsTabTitle',
            ]),
            ...mapGetters('header', ['userUnicodeDateFormat']),
            guestId() {
                return parseInt(this.$route.params.id);
            },
            guestEdit() {
                return this.metadata?.permissions?.guest_edit;
            },
            isMobile() {
                return this.metadata?.is_mobile;
            },
            defaultCountryCode() {
                if (this.guest.mobile_phone) {
                    const phone = parsePhoneNumber(this.guest.mobile_phone);
                    return phone.g.regionCode;
                } else {
                    return this.$store.getters['account/code'];
                }
            },
            title: {
                get() {
                    return this.guest.title?.id || null;
                },
                set(value) {
                    this.guest.title = { id: value };
                },
            },
            titleOptions() {
                return [
                    { value: null, text: '---Not Selected---', disabled: true },
                    { value: 1, text: 'Mr.' },
                    { value: 10, text: 'Mrs.' },
                    { value: 11, text: 'Ms.' },
                    { value: 56, text: 'Mr. & Mrs' },
                    { value: 7301, text: 'Dr.' },
                ];
            },
            minDateOfBirth() {
                return new Date();
            },
            maxDateOfBirth() {
                return subYears(new Date(), 150);
            },
            countriesOptions() {
                if (this.countries)
                    return this.countries.map((item) => ({
                        ...item,
                        value: item.id,
                        text: item.name,
                    }));
                return [];
            },
            statesOptions() {
                if (this.states)
                    return this.states.map((item) => ({
                        ...item,
                        value: item.id,
                        text: item.name,
                    }));
                return [];
            },
            provincesOptions() {
                if (this.provinces)
                    return this.provinces.map((item) => ({
                        ...item,
                        value: item.id,
                        text: item.name,
                    }));
                return [];
            },
            statusesOptions() {
                return [{ value: null, text: '---Not Selected---' }].concat(
                    this.statuses.map((item) => ({
                        ...item,
                        value: item.id,
                        text: item.name,
                    }))
                );
            },
        },
        methods: {
            async loadData() {
                this.loading = true;
                try {
                    await this.fetchGuest();
                    await Promise.all([this.fetchCountries(), this.fetchStatuses()]);
                    // this.$refs.form.setValues(JSON.parse(JSON.stringify(this.guest)));
                } finally {
                    this.loading = false;
                }
            },
            onValidateMobilePhone(value) {
                this.mobilePhoneIsValid = value.valid;
                if (value.valid) {
                    this.guest.mobile_phone = value.number;
                }
            },
            onValidateHomePhone(value) {
                // this.$refs.homePhone.validate();
                this.homePhoneIsValid = value.valid;
                if (value.valid) {
                    this.guest.address.phone = value.number;
                }
            },
            onValidateWorkPhone(value) {
                // this.$refs.workPhone.validate();
                this.workPhoneIsValid = value.valid;
                if (value.valid) {
                    this.guest.address.work_phone = value.number;
                }
            },
            onValidateFax(value) {
                // this.$refs.fax.validate();
                if (value.valid) {
                    this.guest.address.fax = value.number;
                }
            },
            async onChangeCountry() {
                const currentCountry = this.countriesOptions.find(
                    (item) => item.value === this.guest.address.country.id
                );
                this.$store.dispatch('account/changeCountryCode', currentCountry.code);
                this.fetchStates(this.guest.address.country.id);
            },
            onInputMobilePhone(value, field) {
                const obj = JSON.parse(JSON.stringify(this.guest));
                set(obj, field, value);
                this.$refs.form.setFieldValue(field, value);
            },
            handleZipChange: debounce(async function (zip) {
                this.$refs.form.setFieldValue('address.zip', zip);
                if (!this.guest || !this.guest.address.country.id || zip.length < 5) return;
                this.loading = true;
                try {
                    const { data } = await API.get(`/asynchronous/ziptastic/${this.guest.address.country.id}/${zip}`);
                    if (!data || data.message === 'Not Found') throw new Error();
                    this.guest.address.city = data.city;
                    this.states.forEach((state) => {
                        if (state.code === data.state_short) this.guest.address.state.id = state.id;
                    });
                } catch (err) {
                    this.warningToast({
                        text: 'Unable to auto fill city and state.',
                    });
                } finally {
                    this.loading = false;
                }
            }, 500),
            selectTarget(text, value) {
                this.selectedTargetEmail = text;
                this.selectedTargetId = value;
                this.validTarget = true;
                this.targetEmailOptions = [];
                this.$refs.merge_guest_form.setFieldValue('target', text);
            },
            onHideModal() {
                this.selectedTargetEmail = null;
                this.selectedTargetId = null;
                this.validTarget = false;
            },
            searchTargetEmail: debounce(function (value) {
                this.selectedTargetEmail = value;
                this.$refs.merge_guest_form.setFieldValue('target', value);
                this.selectedTargetId = null;
                this.validTarget = false;
                if (value !== '') {
                    this.fetchSubscribers();
                }
            }, 500),
            async fetchMetadata() {
                try {
                    await this.$store.dispatch('guest/fetchMetadata');
                } catch (err) {
                    this.errorToast({ text: 'Could not load metadata.' });
                }
            },
            async fetchGuest() {
                try {
                    await this.$store.dispatch('guest/fetchGuest', {
                        guestId: this.guestId,
                    });
                } catch (err) {
                    this.errorToast({ text: 'Could not load guest data.' });
                }
            },
            async fetchStatuses() {
                try {
                    await this.$store.dispatch('guest/fetchStatuses');
                } catch (err) {
                    this.errorToast({ text: 'Could not load statuses data.' });
                }
            },
            async fetchCountries() {
                try {
                    await this.$store.dispatch('guest/fetchCountries');
                    let found = false;
                    for (let country of this.countries) {
                        if (country.id == this.guest.address?.country?.id) {
                            found = true;
                            break;
                        }
                    }
                    if (!found && this.countries.length) this.guest.address.country.id = this.countries[0].id;
                    this.fetchStates(this.guest.address.country.id);
                } catch (err) {
                    this.errorToast({ text: 'Could not load countries.' });
                }
            },
            async fetchStates(countryId) {
                try {
                    await this.$store.dispatch('guest/fetchStates', {
                        countryId: countryId,
                    });
                    let found = false;
                    for (let state of this.states) {
                        if (state.id == this.guest.address?.state?.id) {
                            found = true;
                            break;
                        }
                    }
                    if (!found && this.states && this.states.length) {
                        this.states.unshift({ id: null, name: '---Not Selected---' });
                        this.guest.address.state.id = this.states[0].id;
                    }
                } catch (err) {
                    this.errorToast({ text: 'Could not load states.' });
                }
            },
            async fetchProvinces(stateId) {
                try {
                    await this.$store.dispatch('guest/fetchProvinces', {
                        stateId: stateId,
                    });
                    var found = false;
                    for (let province of this.provinces) {
                        if (province.id == this.guest.address?.province?.id) {
                            found = true;
                            break;
                        }
                    }
                    if (!found && this.provinces && this.provinces.length)
                        this.guest.address.province = { id: this.provinces[0].id };
                } catch (err) {
                    this.errorToast({ text: 'Could not fetch provinces' });
                }
            },
            async fetchSubscribers() {
                try {
                    this.searchBusy = true;
                    const { data } = await API.get('/system/guests/cp/merge/search', {
                        params: { email: this.selectedTargetEmail },
                    });
                    this.targetEmailOptions = data.guests.map((user) => ({
                        text: `${user.id} - ${user.email}`,
                        value: user.id,
                    }));
                } catch (err) {
                    this.errorToast({ text: 'Could not fetch subscribers.' });
                } finally {
                    this.searchBusy = false;
                }
            },
            async sendMergeGuests() {
                try {
                    const { data } = await API.post(GUEST_MERGE_DIFFERENT_EMAILS_ENDPOINT(), {
                        source_guest_id: this.guest.id,
                        target_guest_id: this.selectedTargetId,
                    });
                    this.$router
                        .replace({ path: `/app/guest/${data.redirect}/edit/` })
                        .catch((err) => {})
                        .finally(() => {
                            this.loadData();
                        });
                    this.$bvModal.hide('merge-guest-modal-id');
                    this.successToast({ text: 'Guests successfully merged.' });
                } catch (err) {
                    this.errorToast({ text: 'Could not merge guests.' });
                }
            },
            async deleteAllBillingData() {
                try {
                    const { data } = await API.post(GUEST_DELETE_ALL_BILLING_DATA_ENDPOINT(this.guestId));
                    this.$bvModal.hide('clear-all-billing-data-modal-id');
                    this.successToast({
                        text: 'Billing data successfully deleted.',
                    });
                } catch (err) {
                    this.errorToast({ text: 'Could not delete billing data.' });
                }
            },
            async saveGuest() {
                try {
                    this.busy = true
                    if (this.guestId) {
                        const { data } = await API.patch(GUEST_BY_ID_ENDPOINT(this.guestId), this.guest);
                        this.guestData = data;
                    } else {
                        const { data } = await API.post(GUEST_CREATE_ENDPOINT(), this.guest);
                        this.guestData = data;
                    }
                    this.$store.dispatch('guest/setGuest', this.guestData);
                    if (this.guestData && this.guestData.address && this.guestData.address.state === null) {
                        this.guest.address.state.id = this.states[0].id;
                    }
                    this.successToast({ text: 'Guest successfully saved.' });
                    if (!this.guestId) {
                        this.$router.push(`/app/guest/${this.guestData.id}/edit/`);
                    }
                } catch (err) {
                    console.log(err);
                    const { generic, fields } = formatResponseError(err);
                    if (!isEmpty(fields)) this.$refs.form.setErrors(fields);
                    this.errorToast({
                        text: generic || 'Could not save guest data.',
                    });
                } finally {
                    this.busy = false
                }
            },
        },
        watch: {
            'guest.address.state.id': function (stateId, previousStateId) {
                if (stateId && stateId !== previousStateId) {
                    this.fetchProvinces(stateId);
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    .guest-edit {
        &__phone {
            width: 100%;

            &.desktop {
                display: block !important;
            }

            :deep(fieldset) {
                width: 100%;
            }

            :deep(a) {
                height: 2.9rem;
                margin-right: 0px !important;
                padding-top: 0.68rem !important;
            }
        }

        &__save-button {
            width: 100px;
        }
    }
</style>
